import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { VaultKeTheme } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(120);
  const [isTokenValid, setIsTokenValid] = useState(true);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Mock user data - in real app this would come from route params
  const userEmail = 'user@example.com';

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          setIsTokenValid(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!resetCode.trim()) {
      setErrors(prev => ({ ...prev, resetCode: 'Reset code is required' }));
      return;
    }

    if (resetCode.trim().length !== 6) {
      setErrors(prev => ({ ...prev, resetCode: 'Reset code must be 6 digits' }));
      return;
    }

    if (!newPassword) {
      setErrors(prev => ({ ...prev, newPassword: 'New password is required' }));
      return;
    }

    if (!confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: 'Please confirm your password' }));
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
      return;
    }

    if (!isTokenValid) {
      setErrors(prev => ({ ...prev, general: 'Reset code has expired' }));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.RESET_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: resetCode.trim(),
          newPassword: newPassword,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Navigate to login page with success message
        alert('Password reset successfully! You can now login with your new password.');
        navigate('/login');
      } else {
        setErrors({ general: data.error || 'Password reset failed' });
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setErrors({ general: 'Network error. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = () => {
    setTimeRemaining(120);
    setIsTokenValid(true);
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
    alert('New reset code sent to your email');
  };

  const styles: { [key: string]: React.CSSProperties } = {
    page: {
      minHeight: '100vh',
      backgroundColor: VaultKeTheme.colors.background,
      color: VaultKeTheme.colors.text,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: VaultKeTheme.spacing.xl,
    },
    container: {
      maxWidth: '500px',
      width: '100%',
    },
    logoContainer: {
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    logo: {
      width: '80px',
      height: '80px',
      backgroundColor: VaultKeTheme.colors.primary,
      borderRadius: '50%',
      margin: '0 auto',
      marginBottom: VaultKeTheme.spacing.lg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '32px',
      fontWeight: 'bold',
      color: VaultKeTheme.colors.background,
    },
    logoText: {
      fontSize: VaultKeTheme.typography.h1.fontSize,
      fontWeight: VaultKeTheme.typography.h1.fontWeight,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    card: {
      backgroundColor: VaultKeTheme.colors.surface,
      borderRadius: VaultKeTheme.borderRadius.xl,
      padding: VaultKeTheme.spacing.xxl,
      boxShadow: VaultKeTheme.shadows.lg,
    },
    header: {
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    title: {
      ...VaultKeTheme.typography.h2,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    subtitle: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
      lineHeight: '1.5',
    },
    countdownCard: {
      backgroundColor: VaultKeTheme.colors.surfaceVariant,
      border: `1px solid ${VaultKeTheme.colors.border}`,
      borderRadius: VaultKeTheme.borderRadius.md,
      padding: VaultKeTheme.spacing.lg,
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xl,
    },
    countdownContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: VaultKeTheme.spacing.sm,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    countdownIcon: {
      fontSize: '28px',
    },
    countdownText: {
      fontSize: '36px',
      fontWeight: 'bold',
      fontFamily: 'monospace',
      color: VaultKeTheme.colors.primary,
    },
    countdownLabel: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
    },
    expiredCard: {
      backgroundColor: VaultKeTheme.colors.error + '15',
      border: `1px solid ${VaultKeTheme.colors.error}`,
      borderRadius: VaultKeTheme.borderRadius.md,
      padding: VaultKeTheme.spacing.lg,
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xl,
    },
    expiredText: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.error,
      fontWeight: '600',
    },
    formGroup: {
      marginBottom: VaultKeTheme.spacing.lg,
    },
    label: {
      display: 'block',
      fontSize: VaultKeTheme.typography.body2.fontSize,
      fontWeight: '600',
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    input: {
      width: '100%',
      padding: VaultKeTheme.spacing.md,
      border: `2px solid ${VaultKeTheme.colors.border}`,
      borderRadius: VaultKeTheme.borderRadius.md,
      backgroundColor: VaultKeTheme.colors.surfaceVariant,
      color: VaultKeTheme.colors.text,
      fontSize: VaultKeTheme.typography.body1.fontSize,
      outline: 'none',
      transition: 'border-color 0.2s ease',
    },
    inputError: {
      borderColor: VaultKeTheme.colors.error,
    },
    passwordContainer: {
      position: 'relative',
    },
    passwordToggle: {
      position: 'absolute',
      right: VaultKeTheme.spacing.md,
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      color: VaultKeTheme.colors.textSecondary,
      cursor: 'pointer',
      fontSize: '18px',
    },
    errorText: {
      fontSize: VaultKeTheme.typography.caption.fontSize,
      color: VaultKeTheme.colors.error,
      marginTop: VaultKeTheme.spacing.xs,
    },
    button: {
      width: '100%',
      padding: VaultKeTheme.spacing.lg,
      backgroundColor: VaultKeTheme.colors.primary,
      color: VaultKeTheme.colors.background,
      border: 'none',
      borderRadius: VaultKeTheme.borderRadius.md,
      fontSize: VaultKeTheme.typography.button.fontSize,
      fontWeight: VaultKeTheme.typography.button.fontWeight,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginBottom: VaultKeTheme.spacing.lg,
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    secondaryButton: {
      width: '100%',
      padding: VaultKeTheme.spacing.lg,
      backgroundColor: 'transparent',
      color: VaultKeTheme.colors.primary,
      border: `1px solid ${VaultKeTheme.colors.primary}`,
      borderRadius: VaultKeTheme.borderRadius.md,
      fontSize: VaultKeTheme.typography.button.fontSize,
      fontWeight: VaultKeTheme.typography.button.fontWeight,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginBottom: VaultKeTheme.spacing.sm,
    },
    resendText: {
      textAlign: 'center',
      fontSize: VaultKeTheme.typography.body2.fontSize,
      color: VaultKeTheme.colors.textSecondary,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    resendLink: {
      color: VaultKeTheme.colors.primary,
      textDecoration: 'none',
      fontWeight: '600',
      cursor: 'pointer',
    },
    backButton: {
      width: '100%',
      padding: VaultKeTheme.spacing.lg,
      backgroundColor: 'transparent',
      color: VaultKeTheme.colors.textSecondary,
      border: 'none',
      borderRadius: VaultKeTheme.borderRadius.md,
      fontSize: VaultKeTheme.typography.button.fontSize,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: VaultKeTheme.spacing.sm,
    },
    backIcon: {
      fontSize: '18px',
    },
  };

  return (
    <div style={{ ...styles.page, paddingBottom: '80px' }}>
      <div style={styles.container}>
        <div style={styles.logoContainer}>
          <div style={styles.logo}>VK</div>
          <h1 style={styles.logoText}>VaultKe</h1>
        </div>

        <div style={styles.card}>
          <div style={styles.header}>
            <h2 style={styles.title}>Reset Password</h2>
            <p style={styles.subtitle}>
              Enter the 6-digit code sent to<br/>
              <strong>{userEmail}</strong><br/>
              and create a new password
            </p>
          </div>

          {isTokenValid ? (
            <div style={styles.countdownCard}>
              <div style={styles.countdownContainer}>
                <span style={styles.countdownIcon}>⏱️</span>
                <span style={styles.countdownText}>{formatTime(timeRemaining)}</span>
              </div>
              <div style={styles.countdownLabel}>Time remaining to reset your password</div>
            </div>
          ) : (
            <div style={styles.expiredCard}>
              <div style={styles.expiredText}>Reset code has expired</div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Reset Code</label>
              <input
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                maxLength={6}
                style={{
                  ...styles.input,
                  ...(errors.resetCode ? styles.inputError : {}),
                }}
              />
              {errors.resetCode && <div style={styles.errorText}>{errors.resetCode}</div>}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>New Password</label>
              <div style={styles.passwordContainer}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  style={{
                    ...styles.input,
                    ...(errors.newPassword ? styles.inputError : {}),
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.passwordToggle}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {errors.newPassword && <div style={styles.errorText}>{errors.newPassword}</div>}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Confirm Password</label>
              <div style={styles.passwordContainer}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  style={{
                    ...styles.input,
                    ...(errors.confirmPassword ? styles.inputError : {}),
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={styles.passwordToggle}
                >
                  {showConfirmPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {errors.confirmPassword && <div style={styles.errorText}>{errors.confirmPassword}</div>}
              {errors.general && <div style={styles.errorText}>{errors.general}</div>}
            </div>

            <button
              type="submit"
              disabled={isLoading || resetCode.length !== 6 || !newPassword || !confirmPassword}
              style={{
                ...styles.button,
                ...(isLoading || resetCode.length !== 6 || !newPassword || !confirmPassword ? styles.buttonDisabled : {}),
              }}
            >
              {isLoading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>

          <div style={styles.resendText}>
            Didn't receive the code?{' '}
            <span style={styles.resendLink} onClick={handleResendCode}>
              Resend Code
            </span>
          </div>

          {!isTokenValid && (
            <button onClick={handleResendCode} style={styles.secondaryButton}>
              Send New Code
            </button>
          )}

          <button
            onClick={() => navigate('/login')}
            style={styles.backButton}
          >
            <span style={styles.backIcon}>←</span>
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;